import type { TextKey } from '../content/fr';
import { startingMarket } from '../content/market';
import type { PleasureCategory } from '../content/treats';
import type { TextParams } from './i18n';
import { nextRandom } from './rng';
import { addYearsISO, todayISO } from './time';

export const SAVE_VERSION = 1;

export type PathId = 'work' | 'alternance' | 'studies';
export type HousingId = 'parents' | 'coloc' | 'studio' | 'proprio';
/** Les biens qu'on peut acheter pour y vivre (voir content/property.ts). */
export type PropertyKind = 't2' | 't3' | 'maison';
/** L'assurance du crédit immobilier : celle de la banque, ou une assurance déléguée, moins chère. */
export type MortgageInsurance = 'banque' | 'deleguee';
export type MilieuId = 'modeste' | 'intermediaire' | 'aise';
export type ProjectId = 'toit' | 'liberte' | 'voyageur' | 'famille' | 'transmettre';
export type StageId = 'prologue' | 'stage1' | 'stage2';
export type PrologueStep =
  | 'gift'
  | 'summer'
  | 'endSummer'
  | 'path'
  | 'housing'
  | 'account'
  | 'project'
  | 'years';
export type ExpenseId =
  | 'logement'
  | 'courses'
  | 'transport'
  | 'forfait'
  | 'abonnements'
  | 'assurance'
  | 'banque'
  | 'assuranceAuto'
  | 'carburant'
  | 'mutuelle'
  | 'personnel'
  | 'charges'
  | 'energie'
  | 'taxeFonciere'
  | 'copropriete';
/** La formule de la mutuelle santé (voir game/health.ts). */
export type MutuelleLevel = 'aucune' | 'base' | 'renforcee';
export type SkillId = 'organisation' | 'communication' | 'technique';
export type PageId =
  | 'premiere_page'
  | 'se_payer_en_premier'
  | 'budget'
  | 'fonds_urgence'
  | 'inflation'
  | 'interet_compose'
  | 'capital_humain'
  | 'cout_credit'
  | 'arnaques'
  | 'train_de_vie'
  | 'loin_des_yeux'
  | 'usure'
  | 'acheter_louer'
  | 'taux_endettement'
  | 'assurance_emprunteur';
export type PalierId = 'cafe' | 'forfait' | 'courses' | 'loyer' | 'toutes';
export type ObjectiveId =
  | 'openLivret'
  | 'firstSaving'
  | 'firstUpgrade'
  | 'skillLevel2'
  | 'savings1000'
  | 'noOverdraft'
  | 'autoSaving'
  | 'emergency'
  | 'cafe'
  | 'firstInvestment'
  | 'monthlyInvestment'
  | 'holdDrop';

export interface Loan {
  id: number;
  label: TextKey;
  principal: number;
  balance: number;
  annualRate: number;
  months: number;
  monthsLeft: number;
  monthlyPayment: number;
  totalInterest: number;
  /** Le prêt étudiant ne se rembourse qu'à partir du premier emploi. */
  deferredUntilJob: boolean;
  /** Le crédit de la résidence principale. */
  mortgage?: boolean;
  /** L'assurance emprunteur, payée chaque mois avec la mensualité : elle ne fait pas baisser la dette. */
  insuranceMonthly?: number;
}

/** La résidence principale achetée. */
export interface OwnedHome {
  kind: PropertyKind;
  /** Le prix payé, et la courbe de l'immobilier ce jour-là : la valeur estimée suit la courbe depuis. */
  price: number;
  indexAtPurchase: number;
  boughtDay: number;
  insurance: MortgageInsurance;
  /** La somme empruntée (0 pour un achat sans crédit). */
  borrowed: number;
  /** Le jour où le crédit a été entièrement remboursé. */
  paidOffDay: number | null;
}

export type SubscriptionId =
  | 'musique'
  | 'series'
  | 'appli_sport'
  | 'magazine'
  | 'autres'
  | 'nouvelle_plateforme'
  | 'salle_de_sport'
  | 'box_soins'
  | 'passe_jeu'
  | 'cinema'
  | 'yoga'
  | 'escalade'
  | 'assurance_telephone'
  | 'appli_budget'
  | 'box_internet';

/** Un abonnement en cours (voir content/subscriptions.ts). */
export interface Subscription {
  id: SubscriptionId;
  /** Prix du mois ; il suit la hausse des prix à chaque bilan. */
  amount: number;
  /** Payé sans servir : la chasse aux économies le retrouve. */
  forgotten?: boolean;
  /** Passé à l'offre avec publicités. */
  withAds?: boolean;
  /** Partagé avec la colocation : c'est la part qu'on paie. */
  shared?: boolean;
}

export interface ExpenseState {
  amount: number;
  /** Nombre de clics « Examiner » déjà dépensés. */
  examined: number;
  optimized: boolean;
  /** Économie mensuelle obtenue en optimisant cette dépense. */
  saved?: number;
}

export interface SkillState {
  xp: number;
  level: number;
}

export interface JournalEntry {
  day: number;
  key: TextKey;
  params?: TextParams;
}

export interface Notice {
  id: number;
  key: TextKey;
  params?: TextParams;
}

export interface EventOutcome {
  key: TextKey;
  params?: TextParams;
}

export interface FollowUp {
  /** L'événement qui arrivera, jamais tiré au sort lui-même. */
  id: string;
  /** Le jour de jeu où il arrivera. */
  day: number;
  /** Les montants de l'événement d'origine, que la suite peut citer. */
  params: Record<string, number>;
}

/** Les placements de la bourse fictive (voir content/market.ts). */
export type AssetId =
  | 'monde'
  | 'maison'
  | 'oblig'
  | 'levier'
  | 'robotique'
  | 'valdore'
  | 'pixelune'
  | 'forges'
  | 'biomyrte'
  | 'morin'
  | 'aurele';
/** Les phases du marché ; la correction est la première baisse garantie. */
export type MarketRegime = 'croissance' | 'bulle' | 'krach' | 'reprise' | 'correction';

/** Le marché fictif (voir game/market.ts). */
export interface MarketState {
  /** Son propre hasard : il ne change pas les autres tirages de la partie. */
  rngState: number;
  regime: MarketRegime;
  /** Mois passés dans la phase, et mois restants pour une phase à durée fixée (krach, reprise, correction). */
  regimeMonths: number;
  monthsLeft: number;
  /** Le dernier jour et le dernier mois de jeu déjà calculés. */
  lastDay: number;
  lastMonth: number;
  /** L'indice « monde », son plus haut, et sa valeur à la fin de chaque mois. */
  index: number;
  indexPeak: number;
  indexHistory: number[];
  /** Le prix d'une part de chaque placement, et sa valeur à la fin de chaque mois. */
  prices: Record<AssetId, number>;
  history: Record<AssetId, number[]>;
  /** La première baisse garantie : le jour prévu (dès le premier achat), et si elle est passée. */
  firstDropDay: number | null;
  firstDropDone: boolean;
  /** La valeur de fond de chaque placement : son cours lissé sur deux ans, qui porte ses bénéfices et son dividende. */
  ema: Record<AssetId, number>;
  emaHistory: Record<AssetId, number[]>;
  indexEma: number;
  /** Les entreprises en pertes qui gagnent enfin de l'argent (Biomyrte, après un traitement autorisé). */
  profitable: Partial<Record<AssetId, boolean>>;
  /** Le taux de base du fonds euros de l'année, annoncé en janvier (avant l'écart propre à chaque contrat). */
  eurosRate: number;
  /**
   * La courbe des prix de l'immobilier (100 au début de la partie), sa valeur à la fin de chaque mois,
   * et les mois de baisse qui restent (0 en temps normal).
   */
  realEstate: number;
  realEstateHistory: number[];
  realEstateSlump: number;
}

/** Des parts d'un placement, et ce qu'elles ont coûté en tout (frais compris). */
export interface Holding {
  shares: number;
  cost: number;
}

/** Le plan d'épargne en actions (voir game/pea.ts). */
export interface PeaState {
  open: boolean;
  openDay: number;
  /** L'argent versé qui attend d'être placé, ou qui vient d'une vente. */
  cash: number;
  /** Les versements cumulés, moins le capital déjà retiré : le plafond de 150 000 € et l'impôt les regardent. */
  deposits: number;
  holdings: Partial<Record<AssetId, Holding>>;
  /** Le versement programmé : un montant par mois, et le placement qu'il achète. */
  monthly: number;
  monthlyAsset: AssetId;
  /** Suspendu : le montant reste réglé, mais rien ne part tant qu'il n'est pas repris. */
  monthlyPaused: boolean;
  /** Le résultat du mois : la valeur au début du mois, l'argent entré ou sorti depuis, et les derniers résultats. */
  valueAtMonthStart: number;
  flows: number;
  lastMonthResult: number;
  results: number[];
  /** La première baisse : ce que valaient les parts ce jour-là, et le niveau du marché, pour le bilan d'un an après. */
  dropStudy?: { value: number; index: number };
  /** Un point par semaine depuis l'ouverture : la valeur du PEA et ce qui a été versé, pour la courbe. */
  history: { value: number; deposits: number }[];
  /** La dernière semaine de jeu déjà notée. */
  historyWeek: number;
}

/** Les deux contrats d'assurance-vie : en ligne, ou celui de la banque (voir game/av.ts). */
export type AvContract = 'online' | 'bank';

/** L'assurance-vie du joueur (voir game/av.ts). */
export interface AvState {
  open: boolean;
  openDay: number;
  contract: AvContract;
  /** Le fonds euros, garanti : chaque versement y arrive, et les ventes d'unités de compte y reviennent. */
  euros: number;
  /** Les intérêts du fonds euros acquis cette année, versés le 31 décembre. */
  eurosAccrued: number;
  /** Les versements cumulés, moins le capital déjà retiré : l'impôt les regarde. */
  deposits: number;
  /** Les unités de compte : des parts des fonds du marché. */
  holdings: Partial<Record<AssetId, Holding>>;
  /** Les gains qui ont déjà payé les prélèvements sociaux : les intérêts versés du fonds euros. */
  taxedGains: number;
  /** Le résultat du mois, comme pour le PEA. */
  valueAtMonthStart: number;
  flows: number;
  lastMonthResult: number;
  results: number[];
  /** Un point par semaine : la valeur du contrat et ce qui a été versé. */
  history: { value: number; deposits: number }[];
  historyWeek: number;
  /** L'abattement de 4 600 € après huit ans : l'année civile, et ce qui en est déjà utilisé. */
  allowanceYear: number;
  allowanceUsed: number;
}

/** Un compte à terme : un versement bloqué jusqu'à l'échéance, à taux brut garanti (voir game/termDeposit.ts). */
export interface TermDeposit {
  id: number;
  amount: number;
  rate: number;
  years: number;
  startDay: number;
  endDay: number;
}

export interface PendingEvent {
  id: string;
  params: Record<string, number>;
  choice: string | null;
  minigame: 'scam' | null;
  outcome: EventOutcome | null;
  /** Bien-être perdu sous le choc quand l'accident arrive (négatif ou nul). */
  shock?: number;
  /** Variation de bien-être due au choix du joueur. */
  wellbeingDelta?: number | null;
  /** Variation de l'argent disponible (compte, tirelire, livret) due au choix du joueur. */
  moneyDelta?: number | null;
  /** Variation d'énergie due au choix du joueur. */
  energyDelta?: number | null;
}

export interface YearStats {
  startDay: number;
  networthStart: number;
  income: number;
  saved: number;
}

export interface ReviewData {
  age: number;
  networth: number;
  networthDelta: number;
  passive: number;
  savingsRate: number;
  /** Tout ce qui a été reçu dans l'année, et ce qui en a été mis de côté (absents des vieux bilans). */
  income?: number;
  saved?: number;
  oldSalary: number;
  salary: number;
  /** Les niveaux de compétence gagnés depuis le bilan précédent (absents des vieux bilans). */
  levelsGained?: number;
  /** Les dépenses du mois avant et après la hausse des prix (absentes des vieux bilans). */
  spendingBefore?: number;
  spendingAfter?: number;
}

export interface OfflineSummary {
  months: number;
  networthBefore: number;
  networthAfter: number;
  interest: number;
  reserveAddedDays: number;
}

export interface GameState {
  version: number;
  rngState: number;
  createdAt: number;
  /** Heure réelle du dernier battement, en millisecondes. */
  lastRealTime: number;

  // Le temps du jeu
  startDate: string;
  day: number;
  lastProcessedMonth: number;
  lastEventWeek: number;
  speed: { dev: number; fastForward: boolean };
  reserveDays: number;

  // Le personnage
  name: string;
  /** Date de naissance ; la partie commence l'été des 18 ans (voir startOfLife). */
  birthDate: string;
  milieu: MilieuId;
  path: PathId | null;
  housing: HousingId | null;
  /** La résidence principale, une fois achetée. */
  home: OwnedHome | null;
  /** Le jour où le tableau de bord s'ouvre, à la fin des choix du prologue (null pour les parties plus anciennes). */
  interfaceDay: number | null;
  project: ProjectId | null;
  stage: StageId;
  prologueStep: PrologueStep;
  firstJobTargetDay: number | null;
  firstJobDay: number | null;
  stageIntro: StageId | null;

  // L'argent
  cash: number;
  piggy: number;
  livret: number;
  /** Intérêts de l'année en cours sur le Livret A, calculés par quinzaine et versés le 31 décembre. */
  accruedInterest: number;
  /** Ce qui rapporte sur le Livret A pendant la quinzaine en cours (les versements attendent la suivante). */
  livretBase: number;
  /** La quinzaine en cours (voir game/livret.ts) et le jour où elle a commencé. */
  lastQuinzaine: number;
  quinzaineStartDay: number;
  /** Le LDDS, qu'on peut ouvrir en plus du Livret A : son solde, ce qui rapporte cette quinzaine, ses intérêts de l'année. */
  lddsOpen: boolean;
  ldds: number;
  lddsBase: number;
  lddsAccrued: number;
  /** Le LEP, réservé aux revenus modestes : ouvert, son solde, ce qui rapporte cette quinzaine, ses intérêts de l'année. */
  lepOpen: boolean;
  lep: number;
  lepBase: number;
  lepAccrued: number;
  /** Les années de suite où les revenus ont dépassé le plafond du LEP : à deux, il est fermé. */
  lepOverYears: number;
  /** Les revenus de l'année civile en cours, puis ceux de l'an dernier : ils décident du droit au LEP. */
  taxYearIncome: number;
  lastTaxYearIncome: number;
  /** Les comptes à terme en cours. */
  termDeposits: TermDeposit[];
  /** La bourse fictive, et le PEA du joueur. */
  market: MarketState;
  pea: PeaState;
  av: AvState;
  totalInterest: number;
  overdraftAccepted: boolean | null;
  /** Agios du mois en cours, calculés chaque jour passé dans le rouge et prélevés en fin de mois. */
  accruedAgios: number;
  /** Frais de découvert non autorisé déjà prélevés ce mois-ci (plafonnés). */
  overdraftFeesThisMonth: number;
  /** La spirale du découvert (voir game/decouvert.ts). */
  /** L'aléa de la tolérance de la banque, propre à la partie (±75 €). */
  overdraftJitter: number;
  /** Mois d'affilée passés au-delà du découvert autorisé. */
  depassementMonths: number;
  /** La carte retirée par la banque : les achats coûtent un peu plus. */
  cardWithdrawn: boolean;
  /** La mise en demeure : 60 jours pour revenir sous 500 € de dépassement, sinon le fichage. */
  miseEnDemeureDay: number | null;
  /** Fiché à la Banque de France : plus aucun crédit. */
  ficp: boolean;
  /** Le plan de surendettement en cours, s'il y en a un. */
  surendettement: { monthsLeft: number } | null;
  /** Exclu de l'école : le salaire de base du métier trouvé, au niveau d'études atteint. */
  jobSalaryBase: number | null;
  loans: Loan[];
  nextLoanId: number;
  salary: number;
  studentIncome: number;
  parentsHelp: number;
  budget: { autoSaving: number; leisure: number };
  /**
   * Les avantages durables d'un abonnement (le cinéma en illimité…) : des points de bien-être visés
   * chaque mois, et la part de l'abonnement qui compte encore comme des envies.
   */
  perks: Record<string, { wellbeing: number; leisure: number }>;
  /** Le niveau des prix : 1 au départ, +2 % à chaque bilan annuel (l'inflation). */
  priceLevel: number;
  expenses: Partial<Record<ExpenseId, ExpenseState>>;
  /** Les abonnements en cours : la ligne « Abonnements » des dépenses en est le total. */
  subscriptions: Subscription[];
  /** La formule de la mutuelle santé : elle décide du reste à charge des soins. */
  mutuelleLevel: MutuelleLevel;

  // Le travail actif
  energy: number;
  energyUnlocked: boolean;
  summerClicks: number;
  summerEarned: number;
  earnedByClick: number;
  hasBike: boolean;
  hasCar: boolean;
  /** Valeur de revente de la voiture : elle compte dans le patrimoine et baisse chaque mois. */
  carValue: number;
  /** L'abonnement de transport d'avant la voiture, rendu si on la revend. */
  transportBeforeCar: number;
  /** La voiture a été achetée neuve : son premier contrôle technique arrive au bout de quatre ans. */
  carBoughtNew: boolean;
  /** Le jour de l'achat de la voiture. */
  carBoughtDay: number;
  /** Encore neuve, pendant ses deux premières années : plaisir du neuf et tarifs du neuf. */
  carNew: boolean;
  /** La fin de la garantie de la voiture : celle du neuf, ou une extension achetée. */
  carWarrantyUntil: number;
  /** Achats de la boutique « Investir en toi » qui font effet. */
  upgrades: string[];
  /** Jour de l'achat, ou du dernier remplacement, de chaque achat de la boutique. */
  upgradeDays: Record<string, number>;
  /** Matériel usé : son bonus s'arrête jusqu'à ce qu'on le remplace. */
  wornUpgrades: string[];
  depletionsThisMonth: number;
  /** Le choc « à plat » a déjà été appliqué ; il se réarme quand l'énergie remonte. */
  lowEnergyHit: boolean;
  fatigueUntilDay: number;
  restUntilDay: number;

  // La carrière
  skills: Record<SkillId, SkillState>;
  focusSkill: SkillId;
  promotions: number;
  lastPromotionDay: number;
  /** Jours d'ancienneté en plus exigés pour la prochaine promotion (après un épuisement). */
  promotionDelay: number;

  // Le savoir et le bien-être
  knowledgePoints: number;
  pages: PageId[];
  unreadPages: PageId[];
  wellbeing: number;
  /** Jours de jeu des derniers petits plaisirs achetés (le plaisir s'use). */
  /** Les jours des plaisirs achetés, par catégorie : ils usent les suivants de la même catégorie. */
  pleasureDays: Partial<Record<PleasureCategory, number[]>>;
  /** Pendant le job d'été, les jours n'avancent pas : on compte en heures travaillées. */

  // Les événements
  pendingEvent: PendingEvent | null;
  /** Envies d'achats inutiles pendant le job d'été, et heure travaillée de la dernière. */
  summerTemptations: number;
  lastTemptationClick: number;
  lastTemptationDay: number;
  lastBluesDay: number;
  lastExhaustionDay: number;
  lastEventDay: number;
  lastBadEventDay: number;
  badStreak: number;
  eventCounts: Record<string, number>;
  /** Dernier jour où chaque événement est arrivé : il ne revient pas trop tôt. */
  eventLastDays: Record<string, number>;
  /** Les suites programmées par un choix (« on en reparle dans deux jours »). */
  followUps: FollowUp[];
  flags: Record<string, boolean>;

  // La progression
  paliers: PalierId[];
  objectivesDone: ObjectiveId[];
  journal: JournalEntry[];
  notices: Notice[];
  nextNoticeId: number;
  yearsReviewed: number;
  /** Le total des niveaux de compétence au premier emploi : les promotions comptent ceux gagnés depuis. */
  levelsAtHire: number;
  /**
   * Le total des niveaux à la dernière augmentation (au bilan ou par une promotion), ou au premier emploi :
   * ceux gagnés depuis rendent l'augmentation annuelle plus probable.
   */
  levelsAtLastRaise: number;
  /** Les bilans passés sans augmentation depuis la dernière (ou depuis le premier emploi). */
  yearsWithoutRaise: number;
  year: YearStats;
  pendingReview: ReviewData | null;
  offlineSummary: OfflineSummary | null;
}

const MILIEUX: MilieuId[] = ['modeste', 'intermediaire', 'aise'];

export function createInitialState(now: number, seed: number = now): GameState {
  const state: GameState = {
    version: SAVE_VERSION,
    rngState: seed | 0,
    createdAt: now,
    lastRealTime: now,

    // Sans date de naissance, la vie commence le 1er juillet de l'année, à 18 ans tout juste.
    startDate: `${todayISO(now).slice(0, 4)}-07-01`,
    day: 0,
    lastProcessedMonth: 0,
    lastEventWeek: 0,
    speed: { dev: 1, fastForward: false },
    reserveDays: 0,

    name: '',
    birthDate: addYearsISO(`${todayISO(now).slice(0, 4)}-07-01`, -18),
    milieu: 'intermediaire',
    path: null,
    housing: null,
    home: null,
    interfaceDay: null,
    project: null,
    stage: 'prologue',
    prologueStep: 'gift',
    firstJobTargetDay: null,
    firstJobDay: null,
    stageIntro: null,

    cash: 0,
    piggy: 0,
    livret: 0,
    accruedInterest: 0,
    livretBase: 0,
    lastQuinzaine: -1,
    quinzaineStartDay: 0,
    lddsOpen: false,
    ldds: 0,
    lddsBase: 0,
    lddsAccrued: 0,
    lepOpen: false,
    lep: 0,
    lepBase: 0,
    lepAccrued: 0,
    lepOverYears: 0,
    taxYearIncome: 0,
    lastTaxYearIncome: 0,
    termDeposits: [],
    market: startingMarket(seed),
    pea: {
      open: false,
      openDay: 0,
      cash: 0,
      deposits: 0,
      holdings: {},
      monthly: 0,
      monthlyAsset: 'monde',
      monthlyPaused: false,
      valueAtMonthStart: 0,
      flows: 0,
      lastMonthResult: 0,
      results: [],
      history: [],
      historyWeek: -1,
    },
    av: {
      open: false,
      openDay: 0,
      contract: 'online',
      euros: 0,
      eurosAccrued: 0,
      deposits: 0,
      holdings: {},
      taxedGains: 0,
      valueAtMonthStart: 0,
      flows: 0,
      lastMonthResult: 0,
      results: [],
      history: [],
      historyWeek: -1,
      allowanceYear: 0,
      allowanceUsed: 0,
    },
    totalInterest: 0,
    overdraftAccepted: null,
    accruedAgios: 0,
    overdraftFeesThisMonth: 0,
    overdraftJitter: 0,
    depassementMonths: 0,
    cardWithdrawn: false,
    miseEnDemeureDay: null,
    ficp: false,
    surendettement: null,
    jobSalaryBase: null,
    loans: [],
    nextLoanId: 1,
    salary: 0,
    studentIncome: 0,
    parentsHelp: 0,
    budget: { autoSaving: 0, leisure: 0 },
    perks: {},
    priceLevel: 1,
    expenses: {},
    subscriptions: [],
    mutuelleLevel: 'base',

    energy: 0,
    energyUnlocked: false,
    summerClicks: 0,
    summerEarned: 0,
    earnedByClick: 0,
    hasBike: false,
    hasCar: false,
    carValue: 0,
    transportBeforeCar: 0,
    carBoughtNew: false,
    carBoughtDay: 0,
    carNew: false,
    carWarrantyUntil: 0,
    // Le smartphone qu'on a déjà à 18 ans, acheté un an plus tôt.
    upgrades: ['telephone'],
    upgradeDays: { telephone: -365 },
    wornUpgrades: [],
    depletionsThisMonth: 0,
    lowEnergyHit: false,
    fatigueUntilDay: 0,
    restUntilDay: 0,

    skills: {
      organisation: { xp: 0, level: 0 },
      communication: { xp: 0, level: 0 },
      technique: { xp: 0, level: 0 },
    },
    focusSkill: 'organisation',
    promotions: 0,
    lastPromotionDay: 0,
    promotionDelay: 0,

    knowledgePoints: 0,
    pages: [],
    unreadPages: [],
    wellbeing: 60,
    pleasureDays: {},

    pendingEvent: null,
    summerTemptations: 0,
    lastTemptationClick: -100,
    lastTemptationDay: -1000,
    lastBluesDay: -1000,
    lastExhaustionDay: -1000,
    lastEventDay: 0,
    lastBadEventDay: -1000,
    badStreak: 0,
    eventCounts: {},
    eventLastDays: {},
    followUps: [],
    // Les nouvelles parties n'ont pas besoin des reprises faites une fois pour les anciennes.
    flags: { usureCourse: true },

    paliers: [],
    objectivesDone: [],
    journal: [],
    notices: [],
    nextNoticeId: 1,
    yearsReviewed: 0,
    levelsAtHire: 0,
    levelsAtLastRaise: 0,
    yearsWithoutRaise: 0,
    year: { startDay: 0, networthStart: 0, income: 0, saved: 0 },
    pendingReview: null,
    offlineSummary: null,
  };
  state.milieu = MILIEUX[Math.floor(nextRandom(state) * MILIEUX.length)];
  // Chaque banque a sa tolérance : un aléa de ±75 € autour de la règle (voir game/decouvert.ts).
  state.overdraftJitter = Math.floor(nextRandom(state) * 151) - 75;
  return state;
}
